import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminChannel } from '@shared/types.ts';
import { api, type ChannelInput } from '@/lib/api.ts';
import { Badge, ConfirmButton, Field, Problem, Switch } from './bits.tsx';

/**
 * THE LINEUP, with the switched-off ones in it.
 *
 * That inclusion is the whole reason this screen does not reuse the viewer's
 * channel list. A viewer's lineup is DEFINED by leaving disabled channels
 * out; an administration screen that does the same is a screen with no way to
 * switch anything back on.
 *
 * FILTERED IN THE BROWSER, not on the server, even though the route accepts a
 * query. A household lineup is a hundred-odd rows — small enough that
 * filtering is instant and, more to the point, filtering locally means
 * reordering does not race a refetch. Typing in the box while a swap is in
 * flight would otherwise hand back the old order.
 */
export function Lineup() {
  const qc = useQueryClient();
  const [kind, setKind] = useState<'all' | 'tv' | 'radio'>('all');
  const [q, setQ] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);

  const { data: channels, isLoading, error } = useQuery({
    queryKey: ['admin', 'channels'],
    queryFn: () => api.adminChannels(),
    staleTime: 5_000,
  });

  const refresh = () => { void qc.invalidateQueries({ queryKey: ['admin', 'channels'] }); };

  /*
   * Sorted by number HERE rather than trusted from the server, because the
   * up/down buttons below are defined against what is on screen. If the two
   * orders ever disagreed, "up" would move a channel somewhere nobody asked.
   */
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (channels ?? [])
      .filter((c) => kind === 'all' || c.kind === kind)
      .filter((c) => !needle
        || c.name.toLowerCase().includes(needle)
        || String(c.number).startsWith(needle)
        || (c.providerName ?? '').toLowerCase().includes(needle))
      .sort((a, b) => a.number - b.number);
  }, [channels, kind, q]);

  const swap = useMutation({
    mutationFn: ({ a, b }: { a: string; b: string }) => api.adminChannelSwap(a, b),
    onSuccess: refresh,
  });

  const bulk = useMutation({
    mutationFn: (enabled: boolean) => api.adminChannelsEnabled(chosen, enabled),
    onSuccess: () => { setChosen([]); refresh(); },
  });

  if (isLoading) return <div className="muted center">Loading the lineup…</div>;
  if (error) return <Problem error={error} />;

  /* Neighbours in the FILTERED view, which is what the server expects: it is
     deliberately ignorant of which list is on screen, so the client names
     both channels in a swap. */
  const move = (index: number, delta: number) => {
    const a = shown[index];
    const b = shown[index + delta];
    if (a && b) swap.mutate({ a: a.id, b: b.id });
  };

  const allChosen = shown.length > 0 && shown.every((c) => chosen.includes(c.id));

  return (
    <>
      <p className="muted admin-note">
        Availability here is server-wide: switching a channel off removes it from
        everybody's lineup, which is a different thing from a viewer hiding it.
        Moving a channel swaps numbers with its neighbour, so nothing else shifts
        underneath somebody who was reading it.
      </p>

      <div className="lineup-bar">
        <div className="kind-tabs">
          {(['all', 'tv', 'radio'] as const).map((k) => (
            <button
              key={k}
              className={`kind-tab${kind === k ? ' is-on' : ''}`}
              onClick={() => setKind(k)}
            >
              {k === 'all' ? 'Everything' : k === 'tv' ? 'TV' : 'Radio'}
            </button>
          ))}
        </div>
        <input
          className="seerr-input lineup-search"
          value={q}
          placeholder="Filter by name, number or provider"
          onChange={(e) => setQ(e.target.value)}
        />
        <span className="muted admin-sub">{shown.length} shown</span>
      </div>

      {chosen.length > 0 && (
        <div className="lineup-bulk">
          <strong>{chosen.length} selected</strong>
          <button className="btn" disabled={bulk.isPending} onClick={() => bulk.mutate(true)}>
            Switch on
          </button>
          <button className="btn" disabled={bulk.isPending} onClick={() => bulk.mutate(false)}>
            Switch off
          </button>
          <button className="btn ghost" onClick={() => setChosen([])}>Clear</button>
        </div>
      )}

      <Problem error={swap.error ?? bulk.error} />

      <table className="admin-table lineup-table">
        <thead>
          <tr>
            <th className="lineup-pick">
              <input
                type="checkbox"
                aria-label="Select everything shown"
                checked={allChosen}
                onChange={(e) => setChosen(e.target.checked ? shown.map((c) => c.id) : [])}
              />
            </th>
            <th>#</th>
            <th>Channel</th>
            <th>Provider</th>
            <th>On</th>
            <th>Order</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {shown.map((c, i) => (
            <ChannelRow
              key={c.id}
              channel={c}
              chosen={chosen.includes(c.id)}
              onChoose={(on) => setChosen(
                on ? [...chosen, c.id] : chosen.filter((x) => x !== c.id),
              )}
              canUp={i > 0}
              canDown={i < shown.length - 1}
              onMove={(d) => move(i, d)}
              busy={swap.isPending}
            />
          ))}
        </tbody>
      </table>

      {shown.length === 0 && <p className="muted">Nothing matches that.</p>}

      {adding
        ? <AddChannel onDone={() => { setAdding(false); refresh(); }} />
        : (
          <button className="btn primary admin-add" onClick={() => setAdding(true)}>
            Add a channel by hand
          </button>
        )}
    </>
  );
}

function ChannelRow({
  channel: c, chosen, onChoose, canUp, canDown, onMove, busy,
}: {
  channel: AdminChannel;
  chosen: boolean;
  onChoose: (on: boolean) => void;
  canUp: boolean;
  canDown: boolean;
  onMove: (delta: number) => void;
  busy: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['admin', 'channels'] }); };

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.adminChannelPatch(c.id, { enabled }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: () => api.adminChannelDelete(c.id),
    onSuccess: refresh,
  });

  return (
    <>
      <tr className={c.enabled ? undefined : 'is-off'}>
        <td className="lineup-pick">
          <input
            type="checkbox"
            checked={chosen}
            aria-label={`Select ${c.name}`}
            onChange={(e) => onChoose(e.target.checked)}
          />
        </td>
        <td className="lineup-number">{c.number}</td>
        <td>
          <div className="lineup-name">
            {c.logo
              ? <img className="lineup-logo" src={c.logo} alt="" loading="lazy" />
              : <span className="lineup-logo is-blank" />}
            <span>
              <span className="admin-name">{c.name}</span>
              <span className="muted admin-sub lineup-meta">
                {c.kind === 'radio' ? 'Radio' : 'TV'} · {c.source}
                {c.categories.length > 0 && <> · {c.categories[0]}</>}
              </span>
            </span>
          </div>
        </td>
        <td className="muted admin-sub">
          {c.providerName ?? <Badge tone="warn">unassigned</Badge>}
        </td>
        <td>
          <Switch
            on={c.enabled}
            disabled={toggle.isPending}
            label={`${c.name} available`}
            onChange={(v) => toggle.mutate(v)}
          />
        </td>
        <td className="lineup-move">
          <button className="btn ghost" disabled={!canUp || busy} onClick={() => onMove(-1)} aria-label={`Move ${c.name} up`}>↑</button>
          <button className="btn ghost" disabled={!canDown || busy} onClick={() => onMove(1)} aria-label={`Move ${c.name} down`}>↓</button>
        </td>
        <td className="lineup-row-actions">
          <button className="btn" onClick={() => setEditing(!editing)}>
            {editing ? 'Close' : 'Edit'}
          </button>
          {/* Two presses. A lineup row is somebody's channel number, their
              favourite and their place in the guide, and there is no undo. */}
          <ConfirmButton disabled={remove.isPending} onConfirm={() => remove.mutate()}>
            Delete
          </ConfirmButton>
        </td>
      </tr>
      {(editing || toggle.isError || remove.isError) && (
        <tr>
          <td colSpan={7} className="lineup-editor">
            <Problem error={toggle.error ?? remove.error} />
            {editing && (
              <ChannelForm
                channel={c}
                onDone={() => { setEditing(false); refresh(); }}
                onCancel={() => setEditing(false)}
              />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Editing one channel.
 *
 * MOUNTS FRESH AND UNMOUNTS ON SAVE, rather than being always-on inputs in
 * the row. Live inputs would have to stay in step with a list that refetches
 * after every swap, and the failure mode is somebody's half-typed name being
 * replaced mid-word by the server's copy. A form that only exists while it is
 * open cannot have that problem.
 */
function ChannelForm({
  channel: c, onDone, onCancel,
}: { channel: AdminChannel; onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState(c.name);
  const [number, setNumber] = useState(String(c.number));
  const [category, setCategory] = useState(c.categories[0] ?? '');
  const [kind, setKind] = useState<'tv' | 'radio'>(c.kind === 'radio' ? 'radio' : 'tv');

  const save = useMutation({
    mutationFn: () => {
      const patch: Partial<ChannelInput> = {};
      if (name.trim() && name.trim() !== c.name) patch.name = name.trim();
      if (number !== String(c.number) && number !== '') patch.number = Number(number);
      if (category !== (c.categories[0] ?? '')) patch.category = category;
      if (kind !== c.kind) patch.kind = kind;
      /* The server refuses an empty patch with a 400 — nothing to change is
         not an error worth showing somebody who pressed Save by habit. */
      if (Object.keys(patch).length === 0) return Promise.resolve(c);
      return api.adminChannelPatch(c.id, patch);
    },
    onSuccess: onDone,
  });

  return (
    <form
      className="provider-form"
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
    >
      <Field label="Name">
        <input className="seerr-input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Number" hint="A number already in use is refused, with whose it is.">
        <input
          className="seerr-input"
          value={number}
          inputMode="numeric"
          onChange={(e) => setNumber(e.target.value.replace(/\D/g, ''))}
        />
      </Field>
      <Field label="Category" hint="How it groups in the guide.">
        <input
          className="seerr-input"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        />
      </Field>
      <Field label="Kind" hint="Decides which permission governs it, and where it appears.">
        <select
          className="seerr-input"
          value={kind}
          onChange={(e) => setKind(e.target.value as 'tv' | 'radio')}
        >
          <option value="tv">TV</option>
          <option value="radio">Radio</option>
        </select>
      </Field>

      <div className="provider-form-actions">
        <button className="btn primary" type="submit" disabled={save.isPending || !name.trim()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
        <button className="btn" type="button" onClick={onCancel}>Cancel</button>
      </div>
      <div className="provider-form-problem"><Problem error={save.error} /></div>
    </form>
  );
}

/**
 * One channel, entered by hand.
 *
 * For the address somebody was given that is not in any playlist — a local
 * encoder, a public stream, the one game on a URL a friend sent. It is the
 * escape hatch that keeps "my provider does not carry it" from being the end
 * of the conversation.
 */
function AddChannel({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [number, setNumber] = useState('');
  const [kind, setKind] = useState<'tv' | 'radio'>('tv');
  const [category, setCategory] = useState('');

  const save = useMutation({
    mutationFn: () => api.adminChannelCreate({
      type: 'custom',
      name: name.trim(),
      customUrl: url.trim(),
      kind,
      category: category.trim(),
      number: number ? Number(number) : undefined,
      enabled: true,
    }),
    onSuccess: onDone,
  });

  const blocked = !name.trim() || !url.trim() || save.isPending;

  return (
    <section className="admin-section">
      <h3 className="admin-subhead">Add a channel by hand</h3>
      <p className="muted admin-note">
        A direct stream address — HLS, MPEG-TS or a plain audio URL. It joins the
        lineup as a “Custom URL” channel and belongs to no provider, so nothing
        will ever sync it away.
      </p>
      <form
        className="provider-form"
        onSubmit={(e) => { e.preventDefault(); if (!blocked) save.mutate(); }}
      >
        <Field label="Name">
          <input className="seerr-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Stream URL">
          <input
            className="seerr-input"
            value={url}
            placeholder="https://example.com/stream.m3u8"
            onChange={(e) => setUrl(e.target.value)}
          />
        </Field>
        <Field label="Number" hint="Blank puts it after the highest you have.">
          <input
            className="seerr-input"
            value={number}
            inputMode="numeric"
            placeholder="auto"
            onChange={(e) => setNumber(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
        <Field label="Kind">
          <select
            className="seerr-input"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'tv' | 'radio')}
          >
            <option value="tv">TV</option>
            <option value="radio">Radio</option>
          </select>
        </Field>
        <Field label="Category">
          <input
            className="seerr-input"
            value={category}
            placeholder="optional"
            onChange={(e) => setCategory(e.target.value)}
          />
        </Field>

        <div className="provider-form-actions">
          <button className="btn primary" type="submit" disabled={blocked}>
            {save.isPending ? 'Adding…' : 'Add'}
          </button>
          <button className="btn" type="button" onClick={onDone}>Cancel</button>
        </div>
        <div className="provider-form-problem"><Problem error={save.error} /></div>
      </form>
    </section>
  );
}
